import { useCallback } from 'react';
import { CdnFile } from '@services/core/cdn/file/file';
import {
    useGetProductMutation,
    usePostProductsMutation,
    usePutProductsMutation,
} from '@services/product/product/product.endpoints';
import { ProductToCreate } from '@services/product/product/product.types';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

export const useAddPageVM = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const type = searchParams.get('type');

    const [product, setProduct] = useState<ProductToCreate>();
    const [productDocuments, setProductDocuments] = useState<CdnFile[]>([]);
    
    const deleteDocument = useCallback(
        (documentId: number) => {
            setProductDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
        },
        [productDocuments]
    );

    const addDocument = useCallback(
        (file: CdnFile) => {
            setProductDocuments((prev) => [...(prev ?? []), file]);
        },
        [productDocuments]
    );

    const [
        getProduct,
        {
            isLoading: isGetProductLoading,
            isError: isGetProductError,
            error: getProductError,
            isSuccess: isGetProductSuccess,
            data: getProductResponse
        }
    ] = useGetProductMutation();

    const [
        createProduct,
        {
            isLoading: isCreateProductLoading,
            isError: isCreateProductError,
            isSuccess: isCreateProductSuccess,
            error: createProductError,
            data: createProductResponse
        }
    ] = usePostProductsMutation();

    const [
        updateProduct,
        {
            isLoading: isUpdateProductLoading,
            isError: isUpdateProductError,
            error: updateProductError,
            isSuccess: isUpdateProductSuccess,
            data: updateProductResponse
        }
    ] = usePutProductsMutation();

    useEffect(() => {
        if (!id) return;
        getProduct({ id });
    }, [id]);

    useEffect(() => {
        if (isUpdateProductLoading) return;
        if (isUpdateProductError) return;
        if (updateProductError) return;

        if (isUpdateProductSuccess && updateProductResponse) {
            navigate(`/products-management/products`);
        }
    }, [isUpdateProductError, isUpdateProductSuccess, updateProductError, updateProductResponse, isUpdateProductLoading, navigate]);

    useEffect(() => {
        if (isCreateProductLoading) return;
        if (isCreateProductError) return;
        if (createProductError) return;

        if (isCreateProductSuccess && createProductResponse) {
            navigate(`/products-management/products`);
        }
    }, [isCreateProductError, isCreateProductSuccess, createProductError, createProductResponse, isCreateProductLoading, navigate]);

    useEffect(() => {
        if (isGetProductLoading) return;
        if (isGetProductError) return;
        if (getProductError) return;

        if (isGetProductSuccess && getProductResponse) {
            setProduct(getProductResponse.data);
            setProductDocuments(getProductResponse.data?.documents || []);
        }
    }, [isGetProductError, isGetProductSuccess, getProductError, getProductResponse, isGetProductLoading]);

    const submitForm = async (values: ProductToCreate) => {
        const finalValues = {
            ...values,
            documents: productDocuments
        };

        if (product && id) {
            await updateProduct({ id: id, product: finalValues });
        } else {
            await createProduct(finalValues);
        }
    };

    return {
        product,
        isEdit: !!id,
        submitForm,
        isLoading: isCreateProductLoading || isGetProductLoading || isUpdateProductLoading,
        isTypeEvent: type === 'event',
        deleteDocument,
        addDocument,
        productDocuments
    };
};